import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError, formatDateTime, formatMoney } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { getRememberedOffenceLabel } from '../../lib/offenceCodes';
import { hasAnyRole, PARKING_ROLES } from '../../lib/roles';
import type { Contravention, EvidenceListItem, ParkingZone, SignedUrlResponse } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, InfoNote, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const EVIDENCE_LABELS: Record<string, string> = {
  PLATE_CLOSEUP: 'Plate close-up',
  VEHICLE_IN_CONTEXT: 'Vehicle in context',
  SIGNAGE: 'Signage',
  WINDSCREEN: 'Windscreen',
  OTHER: 'Other',
};

export function ContraventionDetail() {
  const { id = '' } = useParams();
  const { persona } = useAuth();
  const cq = useApiQuery<Contravention>(`/api/v1/parking/contraventions/${id}`);
  const eq = useApiQuery<{ items: EvidenceListItem[] }>(`/api/v1/parking/contraventions/${id}/evidence`);
  // No GET /zones/:id endpoint exists (see zones.ts) — the full list (already
  // small: a handful of pilot zones) is fetched and joined client-side, same
  // as ContraventionsList.tsx.
  const zq = useApiQuery<{ items: ParkingZone[] }>('/api/v1/parking/zones');

  const canCancel = hasAnyRole(persona?.roles, PARKING_ROLES.PARKING_SUPERVISOR, PARKING_ROLES.PARKING_ADMIN);

  if (cq.loading) return <Spinner label="Loading contravention…" />;
  if (cq.error) return <ErrorNote message={cq.error} />;
  if (!cq.data) return <ErrorNote message="Not found" />;
  const c = cq.data;

  const zone = zq.data?.items.find((z) => z.id === c.zoneId);
  const rememberedOffenceLabel = getRememberedOffenceLabel(c.id);
  const isOwnOfficer = persona?.officerRecord?.id === c.officerId;

  return (
    <div>
      <PageHeader
        title={c.noticeNumber}
        subtitle={`${c.agency} notice — issued ${formatDateTime(c.issuedAt)}`}
        actions={<StatusBadge status={c.status} />}
      />

      <div className="mb-6 grid gap-5 lg:grid-cols-3">
        <div className="card space-y-4 p-5 lg:col-span-2">
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-xs font-semibold uppercase text-slate-400">Registration (as observed)</dt>
              <dd className="font-mono text-slate-800">{c.registrationNumberAsObserved}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase text-slate-400">Zone</dt>
              <dd className="text-slate-800">{zone ? `${zone.code} — ${zone.name}` : c.zoneId}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase text-slate-400">Offence</dt>
              <dd className="text-slate-800">
                {rememberedOffenceLabel ?? <span className="font-mono text-xs text-slate-500">{c.offenceCodeId}</span>}
                {!rememberedOffenceLabel ? (
                  <span className="ml-1.5 block text-[11px] font-normal text-slate-400">
                    m46-parking has no endpoint to resolve an offence code id to a label — this shows the raw reference.
                  </span>
                ) : null}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase text-slate-400">Officer</dt>
              <dd className="font-mono text-xs text-slate-800">
                {c.officerId}
                {isOwnOfficer ? <span className="ml-1.5 font-sans font-semibold text-brand-600">(you)</span> : null}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase text-slate-400">First observed</dt>
              <dd className="text-slate-800">{formatDateTime(c.firstObservedAt)}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase text-slate-400">Observation window</dt>
              <dd className="text-slate-800">{c.observationMinutes} min</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase text-slate-400">GPS</dt>
              <dd className="font-mono text-xs text-slate-800">
                {c.gpsLat.toFixed(5)}, {c.gpsLng.toFixed(5)}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase text-slate-400">Amount</dt>
              <dd className="text-slate-800">
                {formatMoney(c.amountCents)}
                {c.escalatedAmountCents ? <span className="ml-1 text-rose-600">→ escalated {formatMoney(c.escalatedAmountCents)}</span> : null}
              </dd>
            </div>
            {c.overrideReason ? (
              <div className="col-span-2">
                <dt className="text-xs font-semibold uppercase text-slate-400">Supervisor override reason</dt>
                <dd className="text-slate-800">{c.overrideReason}</dd>
              </div>
            ) : null}
            {c.flaggedForReview ? (
              <div className="col-span-2">
                <InfoNote message="Flagged for supervisor review — a prior notice was issued to this plate in this zone within the zone's no-return window." />
              </div>
            ) : null}
            {c.officerNotes ? (
              <div className="col-span-2 border-t border-slate-100 pt-3">
                <dt className="text-xs font-semibold uppercase text-slate-400">Officer notes</dt>
                <dd className="whitespace-pre-wrap text-slate-800">{c.officerNotes}</dd>
              </div>
            ) : null}
            {c.cancelledAt ? (
              <div className="col-span-2 border-t border-slate-100 pt-3">
                <dt className="text-xs font-semibold uppercase text-slate-400">Cancelled</dt>
                <dd className="text-slate-800">
                  {formatDateTime(c.cancelledAt)} by <span className="font-mono text-xs">{c.cancelledBy}</span> — {c.cancellationReason}
                </dd>
              </div>
            ) : null}
          </dl>

          {c.vehicle ? (
            <div className="border-t border-slate-100 pt-3">
              <div className="mb-1 text-xs font-semibold uppercase text-slate-400">Vehicle</div>
              <div className="text-sm text-slate-700">
                {[c.vehicle.make, c.vehicle.model, c.vehicle.colour].filter(Boolean).join(' ') || 'No make/model on file'}
                {c.vehicle.ownerName ? <span className="text-slate-500"> — owner {c.vehicle.ownerName}</span> : null}
              </div>
            </div>
          ) : null}

          <div className="border-t border-slate-100 pt-3">
            <div className="mb-1 text-xs font-semibold uppercase text-slate-400">Chain position</div>
            <div className="font-mono text-xs text-slate-500">
              {c.agency} #{c.seq} · hash {c.hash.slice(0, 16)}…
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <PaymentCard contravention={c} />
          {canCancel ? <CancelCard contravention={c} onDone={cq.reload} /> : null}
        </div>
      </div>

      <EvidenceSection contraventionId={c.id} evidence={eq.data?.items ?? []} loading={eq.loading} error={eq.error} />
    </div>
  );
}

function PaymentCard({ contravention: c }: { contravention: Contravention }) {
  return (
    <div className="card p-4">
      <h2 className="mb-2 text-sm font-bold text-slate-700">Payment</h2>
      <p className="mb-2 text-[11px] text-slate-400">
        Read-only here — initiating payment (POST /contraventions/:id/pay) is a citizen-facing action, out of scope for
        this officer console.
      </p>
      <dl className="space-y-2 text-xs">
        <div className="flex justify-between">
          <dt className="text-slate-400">Pay intent</dt>
          <dd className="font-mono text-slate-700">{c.payIntentRefNo ?? '—'}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-slate-400">Paid at</dt>
          <dd className="text-slate-700">{c.paidAt ? formatDateTime(c.paidAt) : '—'}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-slate-400">Status</dt>
          <dd>
            <StatusBadge status={c.status} />
          </dd>
        </div>
      </dl>
    </div>
  );
}

function CancelCard({ contravention: c, onDone }: { contravention: Contravention; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (c.status === 'CANCELLED') {
    return null;
  }

  const cancel = async () => {
    if (reason.trim().length < 3) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/parking/contraventions/${c.id}/cancel`, { reason: reason.trim() });
      onDone();
      setOpen(false);
      setReason('');
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Cancellation failed');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="card p-4">
        <button className="btn-danger w-full" onClick={() => setOpen(true)}>
          Cancel notice
        </button>
      </div>
    );
  }

  return (
    <div className="card p-4">
      <h2 className="mb-2 text-sm font-bold text-slate-700">Cancel notice</h2>
      <p className="mb-2 text-[11px] text-slate-400">
        The issuing officer cannot cancel their own notice — a different supervisor/admin identity is required (the API
        enforces this).
      </p>
      {error ? (
        <div className="mb-2">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <textarea className="field-input mb-2 min-h-[3rem]" placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
      <div className="flex gap-2">
        <button className="btn-danger flex-1" onClick={cancel} disabled={busy || reason.trim().length < 3}>
          {busy ? 'Cancelling…' : 'Confirm cancel'}
        </button>
        <button className="btn-secondary" onClick={() => setOpen(false)}>
          Back
        </button>
      </div>
    </div>
  );
}

function EvidenceSection({
  contraventionId,
  evidence,
  loading,
  error,
}: {
  contraventionId: string;
  evidence: EvidenceListItem[];
  loading: boolean;
  error: string | null;
}) {
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [viewError, setViewError] = useState<string | null>(null);

  const view = async (evidenceId: string) => {
    setViewingId(evidenceId);
    setViewError(null);
    try {
      const res = await api.get<SignedUrlResponse>(`/api/v1/parking/contraventions/${contraventionId}/evidence/${evidenceId}/signed-url`);
      window.open(res.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setViewError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not fetch a signed URL for this evidence item');
    } finally {
      setViewingId(null);
    }
  };

  return (
    <div className="card p-5">
      <h2 className="mb-3 text-sm font-bold text-slate-700">Evidence ({evidence.length})</h2>
      {error ? <ErrorNote message={error} /> : null}
      {viewError ? (
        <div className="mb-3">
          <ErrorNote message={viewError} />
        </div>
      ) : null}
      {loading ? (
        <Spinner />
      ) : evidence.length === 0 ? (
        <p className="text-xs text-slate-400">No evidence uploaded.</p>
      ) : (
        <ul className="space-y-2">
          {evidence.map((e) => (
            <li key={e.id} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs">
              <div>
                <div className="font-semibold text-slate-700">{EVIDENCE_LABELS[e.kind] ?? e.kind}</div>
                <div className="mt-0.5 text-[11px] text-slate-400">
                  Captured {formatDateTime(e.capturedAt)}
                  {e.uploadedAt ? ` · uploaded ${formatDateTime(e.uploadedAt)}` : ' · not yet uploaded'}
                </div>
              </div>
              <button className="btn-secondary !py-1" onClick={() => view(e.id)} disabled={viewingId === e.id}>
                {viewingId === e.id ? 'Opening…' : 'View'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
